/**
 * Instructor preview of a course unit.
 */
import React, { useEffect, useState } from "react";
import { Icon } from '@iconify/react';
import MuiDrawer from '@mui/material/Drawer';
import { Box, Grid, Divider } from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import BoardSidebar from "./board.sidebar";
import BoardContent from "./board.content";
import BoardProgressBar from "./board.progressbar";
import BoardBreadCrumb from "./board.breadcrumb";
import { useAsterController } from "context";
import { actionAppCourseDetailGet, actionPreviewModeSet } from "context/action";
import { hasVerticalAt } from "utils/course";

/**
 * Renders a unit in preview mode without persisting learner progress.
 */
const BoardPreview = () => {

  const { t } = useTranslation('common');

  const { courseId, sectionIndex, sequenceIndex, verticalIndex } = useParams();
  const navigate = useNavigate();

  const [controller, dispatch] = useAsterController();
  const { mobile, structure, loadedCourseDetail } = controller;

  const [wrap, setWrap] = useState(true);

  useEffect(() => courseId && structure.course_id !== courseId && actionAppCourseDetailGet(dispatch, courseId), [courseId, dispatch, structure.course_id]);
  useEffect(() => actionPreviewModeSet(dispatch, courseId), [courseId, dispatch]);

  useEffect(() => {
    if (!loadedCourseDetail || !structure || structure.course_id !== courseId) return;
    if (!Array.isArray(structure.sections)) return;
    if (hasVerticalAt(structure, sectionIndex, sequenceIndex, verticalIndex)) return;
    navigate(`/course/${courseId}`, { replace: true });
  }, [loadedCourseDetail, structure, courseId, sectionIndex, sequenceIndex, verticalIndex, navigate]);

  return <>
    {
      mobile && <MuiDrawer anchor="left" open={!wrap} onClose={() => setWrap(!wrap)} >
        <Box className="w-80">
          <BoardSidebar setMobileSidebar={setWrap} />
        </Box>
      </MuiDrawer>
    }

    <Box className="m-0 md:m-6 v-bg-body rounded-none md:rounded-md flex-1">
      <Box className="min-h-full mx-auto">
        <Box className="md:px-8 px-4 pt-6">
          <Grid container spacing={2}>
            <Grid item xs={12} className="flex justify-between">
              <Link to={`/course/${courseId}`} className="no-underline">
                <VButton color="secondary" className="text-lg">
                  <Icon icon="bi:chevron-left" className="text-sm flex-shrink-0" />&nbsp;{t("Back")}
                </VButton>
              </Link>
              <VButton onClick={() => setWrap(false)} iconOnly className="block md:hidden">
                <Icon icon="heroicons-solid:menu-alt-1" />
              </VButton>
            </Grid>
            <Grid item xs={12} md={8}>
              <VText className="text-4xl font-bold mt-4" div>{structure.course_title}</VText>
              <BoardBreadCrumb />
            </Grid>
            <Grid item xs={12} md={4} className="flex items-center justify-center md:justify-end">
              <BoardProgressBar />
            </Grid>
          </Grid>
        </Box>
        <Divider className="hidden md:flex mt-5 border-gray-200 dark:border-gray-500" />

        {/******************************* Content ********************************/}
        <Box className="flex justify-between">
          <Box className="hidden md:block z-0">
            <BoardSidebar setMobileSidebar={setWrap} />
          </Box>
          <BoardContent />
        </Box>
        <Divider className="hidden md:flex my-b border-gray-200 dark:border-gray-500" />
      </Box>
    </Box>
  </>;
};

export default BoardPreview;